import {useMemo, useCallback, type DependencyList} from 'react';

import {Debounced, type DebouncedOptions} from './debounced';

/**
 * Debounce a function by 500ms
 *
 * @param callback Function to debounce
 * @param deps The hook's dependencies
 */
export function useDebounce<T extends (...args: any[]) => any>(
    callback: T,
    deps: DependencyList,
): (...args: Parameters<T>) => void;

/**
 * Debounce a function with options
 *
 * @param callback Function to debounce
 * @param options The options passed to the Debounced instance
 * @param deps The hook's dependencies
 */
export function useDebounce<T extends (...args: any[]) => any>(
    callback: T,
    options: DebouncedOptions,
    deps: DependencyList,
): (...args: Parameters<T>) => void;

export function useDebounce<T extends (...args: any[]) => any>(
    callback: T,
    ...rest: [DebouncedOptions, DependencyList] | [DependencyList]
) {
    const [debounced, dependencies] = useMemo(() => {
        if (rest.length === 1) {
            const debounced = new Debounced(callback);
            const dependencies = rest[0];

            return [debounced, dependencies];
        } else {
            const debounced = new Debounced(callback, rest[0]);
            const dependencies = rest[1];

            return [debounced, dependencies];
        }
    }, [callback, rest]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useCallback(debounced.call, dependencies);
}
